import { type Cue, type CueWord, synthesizeWords } from "./cues";
import { containsNoSpaceScript, joinCueTexts, wordTextSpans } from "./text";

/** Smallest cue duration the editor will allow (seconds). */
export const MIN_CUE_DURATION = 0.05;

const clamp = (value: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, value));

export type CueIdFactory = () => string;

export interface CueEditOptions {
  /** Deterministic source for IDs assigned to newly-created cues. */
  createId?: CueIdFactory;
}

export interface WordBoundaryBounds {
  /** Cue start, in seconds. */
  start: number;
  /** Cue end, in seconds. */
  end: number;
}

const withoutWords = (cue: Cue): Omit<Cue, "words"> => {
  const { words: _words, ...rest } = cue;
  return rest;
};

const withoutId = (cue: Cue): Omit<Cue, "id"> => {
  const { id: _id, ...rest } = cue;
  return rest;
};

const withFreshId = (cue: Cue, createId: CueIdFactory | undefined): Cue =>
  createId ? { ...withoutId(cue), id: createId() } : withoutId(cue);

/** Whitespace tokenization, matching {@link synthesizeWords}. */
const tokenize = (text: string): string[] => text.split(/\s+/).filter(Boolean);

interface WordSegment {
  segment: string;
}

interface WordSegmenter {
  segment(input: string): Iterable<WordSegment>;
}

type WordSegmenterConstructor = new (
  locales?: string | string[],
  options?: { granularity: "word" }
) => WordSegmenter;

const segmentNoSpaceText = (text: string): string[] => {
  const Segmenter =
    typeof Intl === "undefined"
      ? undefined
      : (Intl as unknown as { Segmenter?: WordSegmenterConstructor }).Segmenter;
  if (typeof Segmenter === "undefined") {
    const token = text.trim();
    return token ? [token] : [];
  }
  return Array.from(new Segmenter(undefined, { granularity: "word" }).segment(text))
    .map(({ segment }) => segment)
    .filter((segment) => segment.trim().length > 0);
};

/**
 * Re-derive word timing for edited text by aligning the old `words` against the
 * new tokens (longest common subsequence on exact token text). Matched words
 * keep their timing untouched; each changed region redistributes only the local
 * time span between its surviving anchors, proportionally to character length
 * (deleted words' time folds into the region; a 1:1 replacement inherits the
 * old word's exact span). Returns null when nothing aligned — a full rewrite —
 * so the caller can drop `words` and stay in the honest "estimated" state.
 */
function realignWordsToTokens(
  words: CueWord[],
  newTokens: string[],
  cueStart: number,
  cueEnd: number
): CueWord[] | null {
  const n = words.length;
  const m = newTokens.length;
  if (n === 0 || m === 0) return null;

  // lcs(i, j) = LCS length of words[i..] vs newTokens[j..], flat row-major.
  const table = new Array<number>((n + 1) * (m + 1)).fill(0);
  const lcs = (i: number, j: number): number => table[i * (m + 1) + j] ?? 0;
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      table[i * (m + 1) + j] =
        words[i]?.text === newTokens[j]
          ? lcs(i + 1, j + 1) + 1
          : Math.max(lcs(i + 1, j), lcs(i, j + 1));
    }
  }
  if (lcs(0, 0) === 0) return null;

  const out: CueWord[] = [];
  let regionStart = cueStart; // left time boundary of the current changed region
  let pending: string[] = []; // new tokens awaiting timing in that region

  const flush = (regionEnd: number) => {
    if (pending.length === 0) return;
    const span = Math.max(0, regionEnd - regionStart);
    const totalChars = pending.reduce((sum, token) => sum + token.length, 0);
    let cursor = regionStart;
    for (const token of pending) {
      const width = totalChars > 0 ? (span * token.length) / totalChars : 0;
      out.push({ start: cursor, end: cursor + width, text: token });
      cursor += width;
    }
    const last = out[out.length - 1];
    if (last) last.end = Math.max(regionStart, regionEnd); // guard float drift
    pending = [];
  };

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    const word = words[i];
    const token = newTokens[j];
    if (!word || token === undefined) break;
    if (word.text === token && lcs(i, j) === lcs(i + 1, j + 1) + 1) {
      flush(word.start);
      out.push({ ...word });
      regionStart = word.end;
      i += 1;
      j += 1;
    } else if (lcs(i + 1, j) >= lcs(i, j + 1)) {
      i += 1; // old word deleted — its time widens the current region
    } else {
      pending.push(token);
      j += 1;
    }
  }
  for (; j < m; j += 1) {
    const token = newTokens[j];
    if (token !== undefined) pending.push(token);
  }
  flush(cueEnd);
  return out;
}

const commonPrefixLength = (left: string, right: string): number => {
  const limit = Math.min(left.length, right.length);
  let length = 0;
  while (length < limit && left[length] === right[length]) length += 1;
  return length;
};

const commonSuffixLength = (left: string, right: string, prefixLength: number): number => {
  const limit = Math.min(left.length, right.length) - prefixLength;
  let length = 0;
  while (length < limit && left[left.length - length - 1] === right[right.length - length - 1]) {
    length += 1;
  }
  return length;
};

const timedWordsForRegion = (
  tokens: string[],
  regionStart: number,
  regionEnd: number
): CueWord[] => {
  const span = Math.max(0, regionEnd - regionStart);
  const totalChars = tokens.reduce((sum, token) => sum + token.length, 0);
  let cursor = regionStart;
  const words = tokens.map((text) => {
    const width = totalChars > 0 ? (span * text.length) / totalChars : 0;
    const word = { start: cursor, end: cursor + width, text };
    cursor += width;
    return word;
  });
  const last = words[words.length - 1];
  if (last) last.end = Math.max(regionStart, regionEnd);
  return words;
};

/**
 * Preserve whole-word prefix/suffix anchors for no-space scripts, then retime
 * only the edited middle. Character matches that stop inside a word are not
 * anchors: the whole word remains part of the changed region.
 */
function realignNoSpaceWords(cue: Cue, newText: string): CueWord[] | null {
  const words = cue.words;
  if (!words?.length) return null;
  const spans = wordTextSpans(cue.text, words);
  if (!spans) return null;
  const oldText = cue.text.trim();
  const trimStart = cue.text.indexOf(oldText);
  const normalizedSpans = spans.map((span) => ({
    charStart: span.charStart - trimStart,
    charEnd: span.charEnd - trimStart,
  }));

  const prefixLength = commonPrefixLength(oldText, newText);
  const suffixLength = commonSuffixLength(oldText, newText, prefixLength);
  let prefixCount = 0;
  while (
    normalizedSpans[prefixCount] &&
    (normalizedSpans[prefixCount]?.charEnd ?? 0) <= prefixLength
  ) {
    prefixCount += 1;
  }

  const suffixBoundary = oldText.length - suffixLength;
  let suffixStart = words.length;
  while (
    suffixStart > prefixCount &&
    (normalizedSpans[suffixStart - 1]?.charStart ?? Number.NEGATIVE_INFINITY) >= suffixBoundary
  ) {
    suffixStart -= 1;
  }
  if (prefixCount === 0 && suffixStart === words.length) return null;

  const newMiddleStart = prefixCount > 0 ? (normalizedSpans[prefixCount - 1]?.charEnd ?? 0) : 0;
  const oldSuffixStart =
    suffixStart < words.length
      ? (normalizedSpans[suffixStart]?.charStart ?? oldText.length)
      : oldText.length;
  const newMiddleEnd =
    suffixStart < words.length
      ? newText.length - (oldText.length - oldSuffixStart)
      : newText.length;
  const middleTokens = segmentNoSpaceText(newText.slice(newMiddleStart, newMiddleEnd));
  const regionStart = prefixCount > 0 ? (words[prefixCount - 1]?.end ?? cue.start) : cue.start;
  const regionEnd = suffixStart < words.length ? (words[suffixStart]?.start ?? cue.end) : cue.end;

  return [
    ...words.slice(0, prefixCount).map((word) => ({ ...word })),
    ...timedWordsForRegion(middleTokens, regionStart, regionEnd),
    ...words.slice(suffixStart).map((word) => ({ ...word })),
  ];
}

/**
 * Structural cue-editing operations — pure, immutable, UI-agnostic (any editing
 * surface drives the same functions). All take a **sorted** `Cue[]` and return a
 * new sorted `Cue[]`; out-of-range indices are returned unchanged. They keep
 * cues non-overlapping (retime/move clamp to neighbours) and re-derive word
 * timing on text-changing ops via {@link synthesizeWords}.
 */

/**
 * Replace a cue's text, preserving word timing where the words survive.
 * Whitespace-delimited text uses LCS alignment ({@link realignWordsToTokens}).
 * When that has no match in a no-space script, whole-word character
 * prefix/suffix anchors survive and only the changed middle is resegmented. A
 * full rewrite with no anchors drops `words`, falling back to on-demand
 * {@link synthesizeWords}. Empty/whitespace text is a no-op — use
 * {@link deleteCue} to remove a cue.
 */
export function setCueText(cues: Cue[], index: number, text: string): Cue[] {
  const cue = cues[index];
  if (!cue) return cues;
  const trimmed = text.trim();
  if (!trimmed) return cues;
  return cues.map((c, i) => {
    if (i !== index) return c;
    let realigned = c.words
      ? realignWordsToTokens(c.words, tokenize(trimmed), c.start, c.end)
      : null;
    if (
      !realigned &&
      c.words &&
      (containsNoSpaceScript(c.text) || containsNoSpaceScript(trimmed))
    ) {
      realigned = realignNoSpaceWords(c, trimmed);
    }
    return realigned
      ? { ...c, text: trimmed, words: realigned }
      : { ...withoutWords(c), text: trimmed };
  });
}

/** Default length of a freshly-inserted cue (seconds), shrunk to fit the gap. */
const NEW_CUE_DURATION = 1.5;

/**
 * Insert a new (placeholder) cue at `time`, fitted into the surrounding gap so it
 * doesn't overlap. Returns the new sorted array plus the inserted index (so the
 * caller can select/edit it), or null if there's no room. When `time` falls
 * inside an existing cue, the new cue starts at that cue's end.
 */
export function insertCue(
  cues: Cue[],
  time: number,
  duration: number,
  text = "New caption",
  options: CueEditOptions = {}
): { cues: Cue[]; index: number } | null {
  const nextIdx = cues.findIndex((c) => c.start > time);
  const insertAt = nextIdx === -1 ? cues.length : nextIdx;
  const prev = insertAt > 0 ? cues[insertAt - 1] : undefined;
  const next = insertAt < cues.length ? cues[insertAt] : undefined;
  const lo = prev ? prev.end : 0;
  const hi = next ? next.start : duration;
  if (hi - lo < MIN_CUE_DURATION) return null;
  const start = clamp(time, lo, hi - MIN_CUE_DURATION);
  const cue: Cue = {
    start,
    end: Math.min(start + NEW_CUE_DURATION, hi),
    text,
    ...(options.createId ? { id: options.createId() } : {}),
  };
  return { cues: [...cues.slice(0, insertAt), cue, ...cues.slice(insertAt)], index: insertAt };
}

/** Remove the cue at `index`. */
export function deleteCue(cues: Cue[], index: number): Cue[] {
  if (index < 0 || index >= cues.length) return cues;
  return cues.filter((_, i) => i !== index);
}

/**
 * Fit a cue's words to its retimed span: an edge word that TOUCHED the old
 * cue edge follows the new one (extending a cue stretches its first/last
 * word instead of leaving dead air no handle can reach), and every word is
 * clamped inside the new span.
 */
function fitWordsToRetimedCue(
  words: CueWord[],
  previous: { start: number; end: number },
  next: { start: number; end: number }
): CueWord[] {
  return words.map((word, index) => {
    let start = index === 0 && word.start === previous.start ? next.start : word.start;
    let end = index === words.length - 1 && word.end === previous.end ? next.end : word.end;
    start = clamp(start, next.start, next.end);
    end = clamp(end, start, next.end);
    return start === word.start && end === word.end ? word : { ...word, start, end };
  });
}

/**
 * Move a cue by `delta` seconds (drag the whole block), preserving its duration
 * and clamping so it can't cross its neighbours or `[0, duration]`. Word
 * timings ride along.
 */
export function moveCue(cues: Cue[], index: number, delta: number, duration: number): Cue[] {
  const cue = cues[index];
  if (!cue) return cues;
  const span = cue.end - cue.start;
  const lo = cues[index - 1]?.end ?? 0;
  const hi = (cues[index + 1]?.start ?? duration) - span;
  const start = clamp(cue.start + delta, lo, Math.max(lo, hi));
  const applied = start - cue.start;
  return cues.map((c, i) => {
    if (i !== index) return c;
    const words = c.words?.map((word) => ({
      ...word,
      start: word.start + applied,
      end: word.end + applied,
    }));
    return { ...c, start, end: start + span, ...(words ? { words } : {}) };
  });
}

/**
 * Retime one edge of a cue (drag a handle), clamped to its neighbour and a
 * minimum duration; the opposite edge stays put. Words are fitted to the new
 * span ({@link fitWordsToRetimedCue}).
 */
export function retimeCue(
  cues: Cue[],
  index: number,
  edge: "start" | "end",
  value: number,
  duration: number
): Cue[] {
  const cue = cues[index];
  if (!cue) return cues;
  let { start, end } = cue;
  if (edge === "start") {
    start = clamp(value, cues[index - 1]?.end ?? 0, end - MIN_CUE_DURATION);
  } else {
    end = clamp(value, start + MIN_CUE_DURATION, cues[index + 1]?.start ?? duration);
  }
  return cues.map((c, i) => {
    if (i !== index) return c;
    const words = c.words ? fitWordsToRetimedCue(c.words, c, { start, end }) : undefined;
    return { ...c, start, end, ...(words ? { words } : {}) };
  });
}

/**
 * Move the shared boundary between `words[boundaryIndex]` and the next word.
 * The boundary is clamped to the two neighbouring outer edges, so the two edited
 * words remain monotonic and gap-free: left.end === right.start.
 *
 * The OUTER edges are boundaries too: `boundaryIndex === -1` moves the first
 * word's start (clamped to the cue start) and `boundaryIndex === words.length - 1`
 * moves the last word's end (clamped to the cue end) — a word that ends short
 * of the cue is otherwise unreachable.
 */
export function retimeWordBoundary(
  words: CueWord[],
  boundaryIndex: number,
  time: number,
  bounds: WordBoundaryBounds
): CueWord[] {
  const left = words[boundaryIndex];
  const right = words[boundaryIndex + 1];
  if ((!left && !right) || !Number.isFinite(time)) return words;

  const lo = Math.max(bounds.start, left?.start ?? bounds.start);
  const hi = Math.min(bounds.end, right?.end ?? bounds.end);
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi < lo) return words;

  const t = clamp(time, lo, hi);
  return words.map((word, index) => {
    if (index === boundaryIndex && left) return { ...word, end: t };
    if (index === boundaryIndex + 1 && right) return { ...word, start: t };
    return word;
  });
}

/** Apply {@link retimeWordBoundary} to one cue, synthesizing words when needed. */
export function retimeCueWordBoundary(cue: Cue, boundaryIndex: number, time: number): Cue {
  const words = synthesizeWords(cue);
  const nextWords = retimeWordBoundary(words, boundaryIndex, time, {
    start: cue.start,
    end: cue.end,
  });
  return nextWords === words ? cue : { ...cue, words: nextWords };
}

/** Merge the cue at `index` with the next one (text joined, words concatenated). */
export function mergeCues(cues: Cue[], index: number): Cue[] {
  const a = cues[index];
  const b = cues[index + 1];
  if (!a || !b) return cues;
  const merged: Cue = {
    ...a,
    start: a.start,
    end: b.end,
    text: joinCueTexts(a.text, b.text),
    words: [...synthesizeWords(a), ...synthesizeWords(b)],
  };
  return [...cues.slice(0, index), merged, ...cues.slice(index + 2)];
}

/**
 * Split the cue at `index` into two at the word boundary nearest `time`. Words
 * are synthesized when absent. A cue of fewer than two words can't be split and
 * is returned unchanged; the split point is clamped so each side keeps ≥1 word.
 */
export function splitCueAt(
  cues: Cue[],
  index: number,
  time: number,
  options: CueEditOptions = {}
): Cue[] {
  const cue = cues[index];
  if (!cue) return cues;
  const words = synthesizeWords(cue);
  if (words.length < 2) return cues;

  const before = words.filter((w) => (w.start + w.end) / 2 < time).length;
  const count = clamp(before, 1, words.length - 1);
  const first = words.slice(0, count);
  const second = words.slice(count);
  const firstWord = first[0];
  const lastFirst = first[first.length - 1];
  const secondStart = second[0];
  if (!firstWord || !lastFirst || !secondStart) return cues;
  const spans = wordTextSpans(cue.text, words);
  const firstSpan = spans?.[0];
  const lastFirstSpan = spans?.[count - 1];
  const firstSecondSpan = spans?.[count];
  const lastSpan = spans?.[spans.length - 1];
  const firstText =
    firstSpan && lastFirstSpan
      ? cue.text.slice(firstSpan.charStart, lastFirstSpan.charEnd).trim()
      : first.map((word) => word.text).join(" ");
  const secondText =
    firstSecondSpan && lastSpan
      ? cue.text.slice(firstSecondSpan.charStart, lastSpan.charEnd).trim()
      : second.map((word) => word.text).join(" ");

  const firstCue: Cue = {
    ...cue,
    start: cue.start,
    end: lastFirst.end,
    text: firstText,
    words: first,
  };
  const secondCue: Cue = {
    ...withFreshId(cue, options.createId),
    start: secondStart.start,
    end: cue.end,
    text: secondText,
    words: second,
  };
  return [...cues.slice(0, index), firstCue, secondCue, ...cues.slice(index + 1)];
}

/**
 * Partition a cue's real `words` at a text caret. Requires the words to form an
 * in-order partition of the text (else null — the caller falls back). A caret
 * between tokens splits the array at the real word boundary; a caret inside a
 * token splits that one word's span proportionally to the character position,
 * leaving every other word's timing untouched.
 */
function splitWordsAtOffset(
  words: CueWord[],
  text: string,
  offset: number
): { first: CueWord[]; second: CueWord[] } | null {
  const spans = wordTextSpans(text, words);
  if (!spans) return null;
  if (spans.length !== words.length || spans.length < 1) return null;

  for (let k = 0; k < spans.length; k += 1) {
    const span = spans[k];
    const word = words[k];
    if (!span || !word) return null;
    if (offset <= span.charStart) {
      // Boundary caret — split between words k-1 and k.
      if (k === 0) return null; // left side empty (caller's trim check catches this too)
      return { first: words.slice(0, k), second: words.slice(k) };
    }
    if (offset < span.charEnd) {
      // Mid-word caret — divide this word's own span by character position.
      const frac = (offset - span.charStart) / word.text.length;
      const at = word.start + (word.end - word.start) * frac;
      const first = [
        ...words.slice(0, k),
        { start: word.start, end: at, text: word.text.slice(0, offset - span.charStart) },
      ];
      const second = [
        { start: at, end: word.end, text: word.text.slice(offset - span.charStart) },
        ...words.slice(k + 1),
      ];
      return { first, second };
    }
  }
  return null; // caret past the last token — right side is empty
}

/**
 * Split the cue at `index` at a **text caret position** (`charOffset` into the
 * cue's text) — for editing a caption directly. The text is divided there (both
 * sides trimmed; a no-op if either side is empty). When the cue has real word
 * timing, the words are partitioned at the caret and the split lands on the real
 * word boundary (a mid-word caret divides that word's span), preserving all
 * timing — matching what {@link splitCueAt} does for time-based splits. Without
 * words, the duration is apportioned proportionally to the caret's position,
 * clamped so each side keeps {@link MIN_CUE_DURATION}.
 */
export function splitCueAtOffset(
  cues: Cue[],
  index: number,
  charOffset: number,
  options: CueEditOptions = {}
): Cue[] {
  const cue = cues[index];
  if (!cue) return cues;
  const offset = clamp(charOffset, 0, cue.text.length);
  const left = cue.text.slice(0, offset).trim();
  const right = cue.text.slice(offset).trim();
  if (!left || !right) return cues; // caret at an edge — nothing to split into two

  const wordSplit = cue.words ? splitWordsAtOffset(cue.words, cue.text, offset) : null;
  if (wordSplit) {
    const lastFirst = wordSplit.first[wordSplit.first.length - 1];
    const firstSecond = wordSplit.second[0];
    if (lastFirst && firstSecond) {
      const firstCue: Cue = {
        ...cue,
        start: cue.start,
        end: lastFirst.end,
        text: left,
        words: wordSplit.first,
      };
      const secondCue: Cue = {
        ...withFreshId(cue, options.createId),
        start: firstSecond.start,
        end: cue.end,
        text: right,
        words: wordSplit.second,
      };
      return [...cues.slice(0, index), firstCue, secondCue, ...cues.slice(index + 1)];
    }
  }

  if (cue.end - cue.start < MIN_CUE_DURATION * 2) return cues; // too short to halve

  const frac = cue.text.length > 0 ? offset / cue.text.length : 0.5;
  const at = clamp(
    cue.start + (cue.end - cue.start) * frac,
    cue.start + MIN_CUE_DURATION,
    cue.end - MIN_CUE_DURATION
  );
  const firstCue: Cue = { ...withoutWords(cue), start: cue.start, end: at, text: left };
  const secondCue: Cue = {
    ...withFreshId(withoutWords(cue), options.createId),
    start: at,
    end: cue.end,
    text: right,
  };
  return [...cues.slice(0, index), firstCue, secondCue, ...cues.slice(index + 1)];
}
